import React, { useEffect, useState, useRef } from "react"
import { Card, Button, Alert, Form } from "react-bootstrap"
import { useAuth } from "../../contexts/AuthContext"
import { Link, useHistory } from "react-router-dom"
import styled from "styled-components"
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage"
import { updateProfile } from "firebase/auth"
import { onValue, ref as databaseRef } from "firebase/database"
import { db } from "../../util/firebase"
import { getAuthErrorMessage } from "../../util/authErrors"

const ProfileContainer = styled.div`
  max-width: 800px;
  margin: 80px auto 20px;
  padding: 20px;
`;

const ProfileCard = styled(Card)`
  margin-bottom: 20px;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
`;

const ProfileImage = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  overflow: hidden;
  position: relative;
  background-color: #f0f0f0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .upload-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.5);
    color: white;
    text-align: center;
    padding: 5px;
    font-size: 12px;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.2s;
  }
  
  &:hover .upload-overlay {
    opacity: 1;
  }
`;

const ProfileInfo = styled.div`
  flex: 1;
`;

const SubscriptionDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  margin: 16px 0 20px;
`;

const SubscriptionStat = styled.div`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 14px;

  span {
    color: #6c757d;
    display: block;
    font-size: 0.85rem;
    margin-bottom: 4px;
  }

  strong {
    color: #212529;
    text-transform: capitalize;
  }
`;

export default function Dashboard() {
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [loading, setLoading] = useState(false)
  const [photoURL, setPhotoURL] = useState("")
  const [subscription, setSubscription] = useState(null)
  const history = useHistory()
  const { currentUser, logout, updatePassword } = useAuth()
  const fileInputRef = useRef()
  const passwordRef = useRef()
  const passwordConfirmRef = useRef()

  useEffect(() => {
    if (!currentUser) return undefined

    const subscriptionRef = databaseRef(db, `users/${currentUser.uid}/subscription`)
    return onValue(subscriptionRef, (snapshot) => {
      setSubscription(snapshot.val())
    })
  }, [currentUser])

  const subscriptionPlan = subscription?.plan || "Free"
  const subscriptionStatus = subscription?.status || "inactive"
  const hasPaidPlan = subscriptionPlan.toLowerCase() !== "free" && ["active", "trialing"].includes(subscriptionStatus)

  async function handleLogout() {
    setError("")

    try {
      await logout()
      history.push("/login")
    } catch {
      setError("Failed to log out")
    }
  }

  async function handlePasswordChange(e) {
    e.preventDefault()
    
    if (passwordRef.current.value !== passwordConfirmRef.current.value) {
      return setError("Passwords do not match")
    }

    try {
      setError("")
      setSuccess("")
      setLoading(true)
      await updatePassword(passwordRef.current.value)
      setSuccess("Password updated successfully!")
      passwordRef.current.value = ""
      passwordConfirmRef.current.value = ""
    } catch (error) {
      setError(getAuthErrorMessage(error, "We could not update your password. Please try again."))
    }
    setLoading(false)
  }

  async function handlePhotoUpload(e) {
    const file = e.target.files[0]
    if (!file) return

    try {
      setError("")
      setLoading(true)
      
      // Upload to Firebase Storage
      const storage = getStorage()
      const storageRef = ref(storage, `profile_photos/${currentUser.uid}`)
      await uploadBytes(storageRef, file)
      
      // Get download URL
      const downloadURL = await getDownloadURL(storageRef)
      
      // Update profile
      await updateProfile(currentUser, {
        photoURL: downloadURL
      })
      
      setPhotoURL(downloadURL)
      setSuccess("Profile photo updated successfully!")
    } catch (error) {
      setError("Failed to upload photo")
      console.error("Upload error:", error)
    }
    setLoading(false)
  }

  return (
    <ProfileContainer>
      <ProfileCard>
        <Card.Body>
          <ProfileHeader>
            <ProfileImage onClick={() => fileInputRef.current.click()}>
              <img 
                src={photoURL || currentUser?.photoURL || "https://via.placeholder.com/100"} 
                alt="Profile" 
              />
              <div className="upload-overlay">Change Photo</div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handlePhotoUpload}
                accept="image/*"
                style={{ display: "none" }}
              />
            </ProfileImage>
            <ProfileInfo>
              <h2>Profile</h2>
              <p className="text-muted">{currentUser?.email}</p>
            </ProfileInfo>
          </ProfileHeader>

          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}

          <Form onSubmit={handlePasswordChange}>
            <Form.Group id="password">
              <Form.Label>New Password</Form.Label>
              <Form.Control 
                type="password" 
                ref={passwordRef} 
                required 
                placeholder="Enter new password"
              />
            </Form.Group>
            <Form.Group id="password-confirm">
              <Form.Label>Confirm New Password</Form.Label>
              <Form.Control 
                type="password" 
                ref={passwordConfirmRef} 
                required 
                placeholder="Confirm new password"
              />
            </Form.Group>
            <Button 
              disabled={loading} 
              className="w-100 mt-3" 
              type="submit"
            >
              Update Password
            </Button>
          </Form>
        </Card.Body>
      </ProfileCard>

      <ProfileCard>
        <Card.Body>
          <h3>Subscription</h3>
          <p className="text-muted mb-0">
            {hasPaidPlan
              ? "Your paid plan is active for unlimited task planning."
              : "Upgrade to a paid plan for unlimited tasks and premium features."}
          </p>
          <SubscriptionDetails>
            <SubscriptionStat>
              <span>Current plan</span>
              <strong>{subscriptionPlan}</strong>
            </SubscriptionStat>
            <SubscriptionStat>
              <span>Status</span>
              <strong>{subscriptionStatus}</strong>
            </SubscriptionStat>
          </SubscriptionDetails>
          <Button as={Link} to="/pricing" variant={hasPaidPlan ? "outline-primary" : "primary"}>
            {hasPaidPlan ? "View Plans" : "Upgrade Plan"}
          </Button>
        </Card.Body>
      </ProfileCard>
      
      <div className="w-100 text-center mt-2">
        <Button variant="link" onClick={handleLogout}>
          Log Out
        </Button>
      </div>
    </ProfileContainer>
  )
}
